import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Document, Page, pdfjs } from 'react-pdf';
import { useTranslation } from 'react-i18next';
import { getProjectFloorplanFileContent } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrors';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PIN_MARKER_COLORS = [
  '#d32f2f',
  '#1976d2',
  '#ed6c02',
  '#2e7d32',
  '#9c27b0',
  '#00838f',
  '#5d4037',
  '#455a64',
];

function getPinMarkerColor(pinIndex) {
  return PIN_MARKER_COLORS[pinIndex % PIN_MARKER_COLORS.length];
}

function PinNumberBadge({ pinIndex, size = 22 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: getPinMarkerColor(pinIndex),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.45,
        fontWeight: 700,
        border: '2px solid #fff',
        boxShadow: 1,
      }}
    >
      {pinIndex + 1}
    </Box>
  );
}

function FloorplanPinDisplay({ projectId, fileId, mimeType, pins = [] }) {
  const { t } = useTranslation();
  const measureRef = useRef(null);
  const [pageRenderWidth, setPageRenderWidth] = useState(560);
  const [zoom, setZoom] = useState(1);
  const [fileSource, setFileSource] = useState(null);
  const [loadingFile, setLoadingFile] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const isImage = String(mimeType || '').startsWith('image/');

  useEffect(() => {
    if (!projectId || !fileId) {
      setFileSource(null);
      setLoadingFile(false);
      setLoadError(null);
      return undefined;
    }

    let objectUrl = null;
    let cancelled = false;
    setLoadingFile(true);
    setLoadError(null);
    setRenderError(null);

    getProjectFloorplanFileContent(projectId, fileId)
      .then(async (res) => {
        if (cancelled) return;
        const contentType = String(res.headers['content-type'] || '').toLowerCase();
        if (contentType.includes('text/html')) {
          throw new Error('Server returned HTML instead of a floorplan file.');
        }
        if (contentType.includes('application/json')) {
          const text = await res.data.text();
          let message = 'Failed to load floorplan';
          try {
            const parsed = JSON.parse(text);
            message = parsed.error || message;
          } catch {
            message = text || message;
          }
          throw new Error(message);
        }
        const buffer = await res.data.arrayBuffer();
        if (!buffer || buffer.byteLength === 0) {
          throw new Error('Floorplan file is empty');
        }
        const blobType = isImage
          ? (mimeType || contentType || 'image/*')
          : (mimeType || contentType || 'application/pdf');
        const blob = new Blob([buffer], { type: blobType });
        objectUrl = URL.createObjectURL(blob);
        setFileSource(isImage ? { kind: 'image', url: objectUrl } : { kind: 'pdf', url: objectUrl });
      })
      .catch(async (err) => {
        if (cancelled) return;
        setFileSource(null);
        setLoadError(err?.message || await getApiErrorMessage(err, 'Failed to load floorplan'));
      })
      .finally(() => {
        if (!cancelled) setLoadingFile(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, fileId, isImage, mimeType]);

  useEffect(() => {
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (!w) return;
      const next = Math.max(200, Math.min(560, Math.floor(w)));
      setPageRenderWidth((prev) => (prev === next ? prev : next));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const displayWidth = Math.round(pageRenderWidth * zoom);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={t('dailyWorkInput.step3.zoomOut', { defaultValue: 'Zoom out' })}>
            <span>
              <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} disabled={zoom <= 0.5}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title={t('dailyWorkInput.step3.zoomIn', { defaultValue: 'Zoom in' })}>
            <span>
              <IconButton size="small" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))} disabled={zoom >= 3}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={() => setZoom(1)} disabled={zoom === 1}>
            {t('dailyWorkInput.step3.resetZoom', { defaultValue: 'Reset' })}
          </Button>
        </Box>
      </Box>
      <Box
        ref={measureRef}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block', width: displayWidth, maxWidth: 'none' }}>
          {loadingFile && <CircularProgress size={20} />}
          {!loadingFile && (loadError || renderError) && (
            <Typography variant="body2" color="error">{loadError || renderError}</Typography>
          )}
          {!loadingFile && !loadError && !renderError && fileSource?.kind === 'image' && (
            <Box
              component="img"
              src={fileSource.url}
              alt=""
              sx={{ display: 'block', width: displayWidth, maxWidth: 'none', height: 'auto' }}
            />
          )}
          {!loadingFile && !loadError && !renderError && fileSource?.kind === 'pdf' && (
            <Document
              file={fileSource.url}
              loading={<CircularProgress size={20} />}
              onLoadError={(err) => setRenderError(err?.message || 'Failed to render PDF')}
            >
              <Page pageNumber={1} width={displayWidth} renderAnnotationLayer={false} renderTextLayer={false} />
            </Document>
          )}
          {!loadingFile && !loadError && !renderError && fileSource && (
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {(pins || []).map((pin, idx) => (
                <Box
                  key={pin.pinKey || `${idx}-${pin.xPercent}-${pin.yPercent}`}
                  sx={{
                    position: 'absolute',
                    left: `${pin.xPercent}%`,
                    top: `${pin.yPercent}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <PinNumberBadge pinIndex={idx} size={24} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default FloorplanPinDisplay;
